import axios from 'axios'

/**
 * Pull a human-readable message out of a DRF error body.
 *
 * `CustomJSONRenderer` wraps every response - errors included - as
 * `{ data: ..., timestamp }`, so the payload sits one level deeper than axios
 * leaves it. Reading `error.response.data.message` therefore always yields
 * `undefined`, and `toast.error(undefined)` renders nothing at all, which is
 * how a refusal the backend explained carefully reached the user as silence.
 *
 * Inside the wrapper the shape depends on how the backend refused:
 * `{ message }` from a hand-written `Response`, `{ detail }` from a raised
 * `PermissionDenied`/`NotFound`/`AuthenticationFailed`, a bare list from
 * `ValidationError('text')`, and `{ field: ['text'] }` from a serializer.
 */
const IGNORED_KEYS = ['status_code', 'result']

const firstString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined

  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = firstString(entry)
      if (found) return found
    }
    return undefined
  }

  if (value && typeof value === 'object') {
    const body = value as Record<string, unknown>
    // The keys the backend chooses deliberately win over an arbitrary field.
    for (const key of ['message', 'detail']) {
      const found = firstString(body[key])
      if (found) return found
    }
    for (const [key, entry] of Object.entries(body)) {
      // `shared/exception_handler.py` decorates validation errors with these.
      if (IGNORED_KEYS.includes(key)) continue
      const found = firstString(entry)
      if (found) return found
    }
  }

  return undefined
}

/** The error payload with `CustomJSONRenderer`'s `{ data, timestamp }` wrapper
 *  taken off, so `timestamp` can never be read as a message. */
const unwrap = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) return undefined
  const body = error.response?.data
  if (!body || typeof body !== 'object') return body
  const isWrapped = 'data' in body && 'timestamp' in body
  return isWrapped ? (body as { data: unknown }).data : body
}

export const getApiErrorMessage = (error: unknown): string | undefined =>
  firstString(unwrap(error))

/**
 * Whether the backend blamed a named field.
 *
 * A DRF serializer answers `{ field: ['...'] }`, but a view that raises
 * `ValidationError('some text')` answers a bare list with no field on it - so
 * a caller that wants to put the error against its input has to fall back to
 * reading the text. This returns false for that case rather than guessing, and
 * starts returning true for free if the backend ever raises the error against
 * the field instead.
 */
export const hasApiFieldError = (error: unknown, field: string): boolean => {
  const payload = unwrap(error)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false
  }
  return Boolean(firstString((payload as Record<string, unknown>)[field]))
}
